import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  OnDestroy,
  Output,
  signal,
  Signal,
  ViewChild,
  WritableSignal,
} from '@angular/core';
import {
  FormControl,
  FormsModule,
  ReactiveFormsModule,
  UntypedFormControl,
  Validators,
} from '@angular/forms';
import { Subject, Subscription } from 'rxjs';
import { ConfirmationResult, UserCredential } from 'firebase/auth';
import { FirebaseAuthService } from '@app/akita/firebase/services/firebase-auth.service';
import {
  PhoneMetadata,
  PopsyPhoneInputComponent,
} from '../../../components/inputs/phone-input/popsy-phone-input.component';
import { CommonModule } from '@angular/common';
import { TranslocoModule } from '@ngneat/transloco';
import { AkitaRouterQuery } from '@app/akita/router/state/router.query';
import { toSignal } from '@angular/core/rxjs-interop';
import { parseApiError } from '@app/shared/models/api/api-error.model';
import { ArrowLeft1IconComponent } from '../../../shared/components/icons/generated/arrow-left-1/arrow-left-1.component';
import { RemoveIconComponent } from '../../../shared/components/icons/generated/remove/remove.component';
import { LottieAnimationComponent } from '../../../components/lottie-animation/lottie-animation.component';
import {
  logLogin,
  logVerifySMSConfirmed,
  logVerifySMSFailed,
} from '@app/core/services/google-analytics.lazy';
import { GoogleAnalyticsService } from '@app/core/services/google-analytics.service';
import { AkitaFirebaseQuery } from '@app/akita/firebase/state/firebase.query';

@Component({
  selector: 'app-sms-sign-in',
  templateUrl: './sms-sign-in.template.html',
  styleUrls: ['./sms-sign-in.style.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    PopsyPhoneInputComponent,
    ReactiveFormsModule,
    FormsModule,
    TranslocoModule,
    ArrowLeft1IconComponent,
    RemoveIconComponent,
    LottieAnimationComponent,
  ],
})
export class SmsLoginComponent implements OnDestroy {
  @ViewChild('recaptchaContainer', { static: false })
  @Output()
  public readonly handleRedirect: EventEmitter<void> = new EventEmitter();
  @Output() public readonly closeDialog: EventEmitter<boolean> = new EventEmitter();
  @Output() public readonly dialogModeChange: EventEmitter<any> = new EventEmitter();

  public form: FormControl;
  public phoneNumber = '';
  public phoneInputHasError: string | null = null;
  public confirmationResult: WritableSignal<ConfirmationResult | null> = signal(null);
  public loading = false;
  public error: WritableSignal<string | null> = signal(null);

  public isRtlLayout: Signal<boolean>;

  public defaultCountryCode: Signal<string>;

  private readonly destroySubject = new Subject<void>();
  private readonly subs: Subscription[] = [];

  constructor(
    private readonly firebaseAuthService: FirebaseAuthService,
    private readonly akitaRouterQuery: AkitaRouterQuery,
    private readonly analytics: GoogleAnalyticsService,
    private readonly akitaFirebaseQuery: AkitaFirebaseQuery
  ) {
    this.defaultCountryCode = toSignal(this.akitaRouterQuery.selectCountry(), {
      initialValue: 'SA',
    });

    this.isRtlLayout = toSignal(this.akitaRouterQuery.selectIsRtlLayout(), {
      initialValue: false,
    });

    this.form = new UntypedFormControl('', {
      validators: Validators.compose([Validators.required]),
      updateOn: 'change',
    });
  }

  public sendCodeWithPhoneNumber(): void {
    this.loading = true;
    const sub = this.firebaseAuthService
      .signInWithPhoneNumber(
        this.phoneNumber || '',

        false
      )
      .subscribe({
        next: (confirmationResult: ConfirmationResult) => {
          this.loading = false;
          this.confirmationResult.set(confirmationResult);
          console.log('Confirmation result:', confirmationResult);
        },
        error: (err: unknown) => {
          console.error('Error signing in with phone number:', err);
          this.loading = false;
          this.error.set(parseApiError(err).message);
        },
      });

    this.subs.push(sub);
  }

  public confirmCode(): void {
    const otp = this.form.value;
    if (!this.confirmationResult) {
      return;
    }

    this.loading = true;
    if (this.confirmationResult() !== null) {
      const sub = this.firebaseAuthService
        .confirmOtp(this.confirmationResult(), otp)
        .subscribe({
          next: (userCred: UserCredential) => {
            this.loading = false;
            console.log('✅ User signed in:', userCred);
            if (userCred) {
              setTimeout(() => {
                const user = this.akitaFirebaseQuery.user;
                logLogin(this.analytics.auth, user, 'phone number');
                logVerifySMSConfirmed(this.analytics.logEventWrapper);

                this.handleRedirect.next();
                this.closeDialog.emit(false);
                // eslint-disable-next-line @typescript-eslint/no-magic-numbers
              }, 2000);
            }
          },
          error: (err: unknown) => {
            const apiError = parseApiError(err);
            this.loading = false;
            logVerifySMSFailed(
              this.analytics.logEventWrapper,
              'Error confirming OTP:',
              apiError.message,
              'phone-sign-in'
            );
            console.error('Error confirming OTP:', err);
          },
        });

      this.subs.push(sub);
    }
  }

  public getMaskedNumber(): string | null {
    if (!this.phoneNumber) {
      return null;
    }
    return this.phoneNumber.replace(/.(?=.{3,}$)/g, (match) =>
      match === ' ' ? ' ' : 'X'
    );
  }

  public onPhoneChange(phone: PhoneMetadata | null): void {
    this.phoneNumber = phone?.display || '';
  }

  public onPhoneError(hasError: string | null): void {
    this.phoneInputHasError = hasError;
  }

  public changeDialogMode(mode?: string | null): boolean {
    /* const username: string | null =
      this.params.username ||
      this.queryParams.username ||
      this.placeholderUsername ||
      null;
    let email: string | null = null;
    const emailField = this.signInForm.get('email');
    if (emailField && emailField.status === 'VALID') {
      email = emailField.value;
    } */

    this.dialogModeChange.emit({
      mode: mode || 'sign-in',
      email: '',
      username: '',
      previousMode: 'auth-intro',
    });

    return false;
  }

  public close(dismiss?: boolean): boolean {
    this.closeDialog.emit(dismiss);
    return false;
  }

  public ngOnDestroy(): void {
    this.destroySubject.next();
    this.destroySubject.complete();
    this.subs.forEach((s) => s.unsubscribe());
  }
}
